// @flow
import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import JobOrderForm from 'UI/components/organisms/JobOrderForm';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { prepareFormToSubmit } from 'UI/utils/forms';

import { FormFieldsMap } from '../JobOrderForm/fields';

type JobOrderEditProps = {
  joborder: any,
  onEditCompleted: (profile: any) => void,
  onEditClosed: () => void
};

const JobOrderEdit = ({ joborder, onEditCompleted, onEditClosed }: JobOrderEditProps) => {
  const {
    id,
    title,
    source,
    sourceType,
    different_location,
    address,
    company,
    specialty,
    subspecialty,
    position,
    keywordTags
  } = joborder;
  const zip = address?.zip;
  const city = address?.city;
  const state = address?.city?.state;

  const { showAlert } = useShowAlert();
  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isFormDisabled: false,
    isReadOnly: false
  });

  const form = useForm(
    joborder
      ? {
          defaultValues: {
            id,
            title,
            source,
            source_type: sourceType,
            company,
            specialty,
            subspecialty,
            functionalTitle: position,
            different_location,
            state_id: state,
            city_id: city,
            zip: { id: zip, title: zip },
            keywordTags
          }
        }
      : {}
  );

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const prepareJobOrder = prepareFormToSubmit(formData, FormFieldsMap);
      const response = await API.put(`${Endpoints.JobOrders}/${id}`, prepareJobOrder);
      if (response.data && response.status === 201) {
        showAlert({
          severity: 'success',
          title: strings.inventoryProfiles.sections.jobOrders.updateProfile
        });

        setUiState(prevState => ({
          ...prevState,
          isSaving: false,
          isSuccess: true
        }));

        const updatedProfile = response.data;
        onEditCompleted(updatedProfile);
      }
    } catch (error) {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: false
      }));
      showAlert({
        title: strings.inventory.jobOrders.create.title,
        error
      });
    }
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: onEditClosed,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, onClose: onEditClosed }}
      CardHeaderProps={{ title: strings.inventoryProfiles.common.editProfile }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: form.handleSubmit(onSubmit) }}
      onClose={onEditClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>
        <JobOrderForm isEditing />
      </FormContext>
    </FPDrawer>
  );
};

export default JobOrderEdit;
